import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import axios from "axios";
import config from "../Config/config";
import { showToast } from "../Common/ToastAlert";

// ---- Intake schema (based on your PDF) ----
const INTAKE_FIELDS = [
  { key: "howDidYouFindUs", label: "How did you find us?", type: "text", section: "Company Info" },
  { key: "companyNameAndWebsite", label: "Company Name & Website", type: "text", section: "Company Info" },
  { key: "mainContactName", label: "Main Contact Name", type: "text", section: "Company Info" },
  { key: "mainContactEmail", label: "Main Contact Email", type: "text", section: "Company Info" },
  { key: "mainContactPhone", label: "Main Contact Phone", type: "text", section: "Company Info" },
  { key: "additionalContacts", label: "Additional Contacts", type: "text", section: "Company Info" },
  { key: "additionalContactEmail", label: "Additional Contact Email", type: "text", section: "Company Info" },
  { key: "additionalContactPhone", label: "Additional Contact Phone", type: "text", section: "Company Info" },

  { key: "whereSellOnline", label: "Where do you currently sell online?", type: "text", section: "Sales" },
  { key: "productTypes", label: "What types of products do you sell?", type: "text", section: "Sales" },
  { key: "runOnlineAuctions", label: "Do you run Online Auctions?", type: "boolean", section: "Sales" },
  { key: "lotsPerWeek", label: "How many Lots do you run per week?", type: "text", section: "Sales" },
  { key: "itemsPerWeek", label: "How many Items do you post per week?", type: "text", section: "Sales" },
  { key: "peopleCreateListings", label: "How many people create listings for you?", type: "text", section: "Sales" },

  { key: "sellNewMarketplaces", label: "Do you want to sell on any new marketplaces?", type: "boolean", section: "Goals" },
  { key: "runRetailStore", label: "Do you run a retail store?", type: "boolean", section: "Goals" },
  { key: "openToChange", label: "Are you open to change?", type: "boolean", section: "Goals" },
  { key: "scaleOrGrow", label: "Do you want to scale or grow?", type: "boolean", section: "Goals" },
  { key: "cutOverhead", label: "Do you want to cut overhead?", type: "boolean", section: "Goals" },
  { key: "doMoreWithWhatYouHave", label: "Do you want to do more with what you have?", type: "boolean", section: "Goals" },
  { key: "goalsHowWeCanHelp", label: "What are your goals for how we can help?", type: "text", section: "Goals" },

  {
    key: "recommendation",
    label: "Recommendation",
    type: "enum",
    section: "Recommendation",
    options: ["Explorer", "Basic", "Professional", "Managed Services"],
  },
  { key: "overallNotes", label: "Notes", type: "text", section: "Recommendation" },

  { key: "oneTrainingCallEnough", label: "Is ONE Online Training Call enough?", type: "boolean", section: "Fit" },
  { key: "onboardingChatSupportSufficient", label: "Is Onboarding Chat Support sufficient?", type: "boolean", section: "Fit" },
  { key: "topAiPromptsSufficient", label: "Are 5 Top AI Prompts sufficient?", type: "boolean", section: "Fit" },

  { key: "requirePhoneSupport", label: "Do they Require Phone Support?", type: "boolean", section: "Requirements" },
  { key: "basicIntegrationsSufficient", label: "Are the Basic Integrations Sufficient?", type: "boolean", section: "Requirements" },
  { key: "needConsignorData", label: "Do they need Consignor Data?", type: "boolean", section: "Requirements" },
  { key: "needWarehouseLocationsInventory", label: "Do they need Warehouse Locations / Inventory?", type: "boolean", section: "Requirements" },
  { key: "needCustomConditions", label: "Do they need Custom Conditions?", type: "boolean", section: "Requirements" },
  { key: "needDeveloperSupport", label: "Do they want or need Developer Support?", type: "boolean", section: "Requirements" },
  { key: "wantAnalyticsUsersItems", label: "Do they want Analytics by Users / Items?", type: "boolean", section: "Requirements" },
  { key: "joinOnlineTrainingSessions", label: "Do they want to join Online Training Sessions?", type: "boolean", section: "Requirements" },
  { key: "joinUserGroup", label: "Do they want to join a User Group?", type: "boolean", section: "Requirements" },
  { key: "wantEmailMarketingProgram", label: "Do they want an Email Marketing Program?", type: "boolean", section: "Requirements" },
  { key: "hibidPicklist", label: "If on HiBid, do they want a Picklist?", type: "boolean", section: "Requirements" },
  { key: "aiPromptTrainingCustomTones", label: "Do they want AI Prompt Training - Custom Prompts?", type: "boolean", section: "Requirements" },
  { key: "needCustomSubconditions", label: "Do they need Custom Subconditions?", type: "boolean", section: "Requirements" },
  { key: "needCustomCategories", label: "Do they need Custom Categories?", type: "boolean", section: "Requirements" },
  { key: "needCsvImportsMigrations", label: "Do they need CSV Imports (Migrations)?", type: "boolean", section: "Requirements" },
  { key: "needCustomIntegrationsOrApi", label: "Do they need Custom Integrations or an API***?", type: "boolean", section: "Requirements" },
  { key: "interestInEcommerce", label: "Do they have interest in E-Commerce?", type: "boolean", section: "Requirements" },
  { key: "foundationMeetBiMonthly", label: "Does a Foundation Meet (Bi-Monthly) make sense?", type: "boolean", section: "Requirements" },
  { key: "wantTrainingOnSite", label: "Do they want Training - On Site***?", type: "boolean", section: "Requirements" },
  { key: "reviewSortingLogicProcess", label: "Do they want to review Sorting Logic Process?", type: "boolean", section: "Requirements" },

  { key: "needHibidSiteBuildOut", label: "Do they need a HiBid Auction Site Build Out?", type: "boolean", section: "Other" },
  { key: "needRetailBlueprintMapLabeling", label: "Do they need a Retail Blueprint Map & Labeling?", type: "boolean", section: "Other" },
  { key: "needShopifySystemOverhaul", label: "Do they need a Shopify System Overhaul?", type: "boolean", section: "Other" },
  { key: "needShopifyPos", label: "Do they need Shopify POS?", type: "boolean", section: "Other" },
  { key: "doOnlineMultiChannelEcommerce", label: "Do they do Online Multi-Channel E-Commerce?", type: "boolean", section: "Other" },
  { key: "other", label: "OTHER", type: "text", section: "Other" },
];

const makeInitialResponses = () => {
  const obj = {};
  for (const f of INTAKE_FIELDS) {
    obj[f.key] = {
      response: f.type === "boolean" || f.type === "enum" ? null : "",
      notes: "",
    };
  }
  return obj;
};

// Merge server responses into defaults so new fields always exist.
const mergeResponses = (defaults, incoming) => {
  const out = { ...defaults };
  if (!incoming || typeof incoming !== "object") return out;

  Object.keys(out).forEach((k) => {
    const v = incoming[k];
    if (v && typeof v === "object") {
      out[k] = {
        response: v.response !== undefined ? v.response : out[k].response,
        notes: v.notes !== undefined ? v.notes : out[k].notes,
      };
    }
  });

  // keep any unknown keys server has (future proof)
  Object.keys(incoming).forEach((k) => {
    if (!out[k]) out[k] = incoming[k];
  });

  return out;
};

export default function IntakeForm({ email }) {
  const cleanEmail = String(email || "").trim().toLowerCase();

  const [form, setForm] = useState(() => makeInitialResponses());
  const [saving, setSaving] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [exists, setExists] = useState(false); // just for label / info

  const sections = useMemo(() => {
    const map = new Map();
    for (const f of INTAKE_FIELDS) {
      if (!map.has(f.section)) map.set(f.section, []);
      map.get(f.section).push(f);
    }
    return Array.from(map.entries());
  }, []);

  // Load existing intake when email changes
  useEffect(() => {
    const run = async () => {
      // reset when changing users
      setForm(makeInitialResponses());
      setExists(false);
      setLoadedOnce(false);

      if (!cleanEmail) {
        setLoadedOnce(true);
        return;
      }

      setLoading(true);
      try {
        const res = await axios.get(`${config.API_BASE_URL}/intake-form`, {
          params: { email: cleanEmail },
        });

        if (res?.data?.success) {
          const defaults = makeInitialResponses();
          const hydrated = mergeResponses(defaults, res.data.responses);
          setForm(hydrated);
          setExists(true);
        }
      } catch (err) {
        // 404 => no intake yet (new record)
        if (err?.response?.status !== 404) {
          console.error("Failed to load intake form", err);
          showToast("Failed to load intake form", "error");
        }
      } finally {
        setLoading(false);
        setLoadedOnce(true);
      }
    };

    run();
  }, [cleanEmail]);

  const setFieldResponse = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), response: value },
    }));
  };

  const setFieldNotes = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), notes: value },
    }));
  };

  const handleSave = async () => {
    if (!cleanEmail) {
      showToast("Missing email", "error");
      return;
    }

    try {
      setSaving(true);
      const payload = { email: cleanEmail, responses: form };
      const res = await axios.post(`${config.API_BASE_URL}/intake-form`, payload);

      if (res?.data?.success) {
       
        setExists(true);
      } else {
        showToast("Save failed.", "error");
      }
    } catch (err) {
      console.error("Save intake error:", err);
      showToast("Failed to save intake.", "error");
    } finally {
      setSaving(false);
    }
  };

  // --- shared UI styles ---
  const fieldCardSx = {
    borderRadius: "1.25rem",
    border: "1px solid rgba(148,163,184,0.45)",
    background: "linear-gradient(to bottom right, rgba(255,255,255,0.95), rgba(248,250,252,0.92))",
    boxShadow: "0 10px 22px rgba(15,23,42,0.08)",
    p: { xs: 1.5, sm: 2 },
  };

  const labelSx = {
    fontSize: { xs: "0.95rem", sm: "1.02rem" },
    fontWeight: 900,
    color: "#0f172a",
    lineHeight: 1.25,
  };

  const answerFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.95rem",
      backgroundColor: "rgba(241,245,249,0.65)",
      "& fieldset": { borderColor: "rgba(51,57,153,0.25)" },
      "&:hover fieldset": { borderColor: "rgba(51,57,153,0.55)" },
      "&.Mui-focused fieldset": { borderColor: "#333999", borderWidth: 2 },
    },
  };

  const notesFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.95rem",
      backgroundColor: "rgba(255,255,255,0.92)",
      "& fieldset": { borderColor: "rgba(148,163,184,0.55)" },
      "&:hover fieldset": { borderColor: "rgba(148,163,184,0.9)" },
      "&.Mui-focused fieldset": { borderColor: "rgba(99,102,241,0.9)", borderWidth: 2 },
    },
  };

  const renderField = (f) => {
    const v = form?.[f.key]?.response;
    const notes = form?.[f.key]?.notes;

    const headerRow = (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={labelSx}>{f.label}</Typography>
        <Chip
          size="small"
          label={f.type === "boolean" ? "Yes/No" : f.type === "enum" ? "Select" : "Text"}
          sx={{
            fontWeight: 900,
            borderRadius: "999px",
            bgcolor: "rgba(51,57,153,0.10)",
            color: "#333999",
            border: "1px solid rgba(51,57,153,0.25)",
          }}
        />
      </Box>
    );

    if (f.type === "boolean") {
      return (
        <Box sx={fieldCardSx}>
          {headerRow}

          <Box
            sx={{
              mt: 1.25,
              p: 1,
              borderRadius: "1rem",
              border: "1px solid rgba(51,57,153,0.18)",
              background: "linear-gradient(to right, rgba(51,57,153,0.06), rgba(85,85,187,0.06))",
            }}
          >
            <RadioGroup
              row
              value={v === null ? "" : v ? "yes" : "no"}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "yes") setFieldResponse(f.key, true);
                else if (val === "no") setFieldResponse(f.key, false);
                else setFieldResponse(f.key, null);
              }}
              sx={{
                gap: 2,
                "& .MuiFormControlLabel-label": { fontWeight: 700 },
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </Box>

          <TextField
            label="Internal notes"
            value={notes || ""}
            onChange={(e) => setFieldNotes(f.key, e.target.value)}
            size="small"
            multiline
            minRows={2}
            placeholder="Add context, objections, follow-ups..."
            sx={{ mt: 1.25, ...notesFieldSx }}
            fullWidth
          />
        </Box>
      );
    }

    if (f.type === "enum") {
      return (
        <Box sx={fieldCardSx}>
          {headerRow}

          <FormControl size="small" sx={{ mt: 1.25 }} fullWidth>
            <InputLabel id={`${f.key}-label`}>{f.label}</InputLabel>
            <Select
              labelId={`${f.key}-label`}
              label={f.label}
              value={v ?? ""}
              onChange={(e) => setFieldResponse(f.key, e.target.value || null)}
              sx={{
                borderRadius: "0.95rem",
                backgroundColor: "rgba(241,245,249,0.65)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(51,57,153,0.25)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(51,57,153,0.55)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#333999", borderWidth: 2 },
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {f.options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Internal notes"
            value={notes || ""}
            onChange={(e) => setFieldNotes(f.key, e.target.value)}
            size="small"
            multiline
            minRows={2}
            placeholder="Why this plan? Any pricing sensitivity?" 
            sx={{ mt: 1.25, ...notesFieldSx }}
            fullWidth
          />
        </Box>
      );
    }

    return (
      <Box sx={fieldCardSx}>
        {headerRow}

        <TextField
          label="Answer"
          value={v || ""}
          onChange={(e) => setFieldResponse(f.key, e.target.value)}
          size="small"
          fullWidth
          sx={{ mt: 1.25, ...answerFieldSx }}
          placeholder="Type the customer response..."
        />

        <TextField
          label="Internal notes"
          value={notes || ""}
          onChange={(e) => setFieldNotes(f.key, e.target.value)}
          size="small"
          multiline
          minRows={2}
          placeholder="Add context, next steps, callouts..."
          sx={{ mt: 1.25, ...notesFieldSx }}
          fullWidth
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "1.5rem",
        border: "1px solid rgba(148,163,184,0.45)",
        background: "linear-gradient(to bottom right, #f9fafb, #eff6ff, #eef2ff)",
        boxShadow: "0 18px 40px rgba(15,23,42,0.12)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "1.35rem", sm: "1.75rem" },
              fontWeight: 1000,
              letterSpacing: "-0.02em",
              color: "#333999",
              lineHeight: 1.1,
            }}
          >
            Intake Form
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", color: "#64748b", mt: 0.5 }}>
            {exists ? "Existing intake loaded (updates on save)." : "No existing intake found (creates on save)."}
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !cleanEmail}
          sx={{
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 900,
            px: 3,
            background: "linear-gradient(to right, #333999, #5555bb)",
            boxShadow: "0 10px 24px rgba(51,51,153,0.35)",
            "&:hover": { background: "linear-gradient(to right, #2a2a7a, #4b4bb0)" },
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {!cleanEmail && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: "1rem" }}>
          Missing email. Open this from Admin using the Intake Form button.
        </Alert>
      )}

      <TextField
        label="Customer Email"
        value={cleanEmail}
        size="small"
        fullWidth
        InputProps={{ readOnly: true }}
        sx={{ mb: 2, ...notesFieldSx }}
      />

      {loading && !loadedOnce && (
        <Box sx={{ py: 2, textAlign: "center" }}>
          <CircularProgress size={28} />
          <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "0.9rem", fontWeight: 700 }}>
            Loading intake...
          </Typography>
        </Box>
      )}

      {sections.map(([sectionName, fields], idx) => (
        <Box key={sectionName} sx={{ mb: 3.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.25,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.15rem", sm: "1.35rem" },
                fontWeight: 1000,
                letterSpacing: "-0.01em",
                color: "#0f172a",
              }}
            >
              {sectionName}
            </Typography>
            <Chip
              size="small"
              label={`${fields.length} items`}
              sx={{
                fontWeight: 900,
                borderRadius: "999px",
                bgcolor: "rgba(148,163,184,0.20)",
                color: "#334155",
                border: "1px solid rgba(148,163,184,0.35)",
              }}
            />
          </Box>

          <Box sx={{ display: "grid", gap: 2 }}>
            {fields.map((f) => (
              <Box key={f.key}>{renderField(f)}</Box>
            ))}
          </Box>

          {idx !== sections.length - 1 && (
            <Divider
              sx={{
                mt: 3,
                borderColor: "rgba(148,163,184,0.45)",
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}
